import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { MEDIA } from '@/theme/tokens';

/** Media query as state. Server-safe default of false; the effect corrects on mount. */
export function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = useState<boolean>(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false;
    return window.matchMedia(query).matches;
  });

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return;
    const list = window.matchMedia(query);
    const onChange = (event: MediaQueryListEvent) => setMatches(event.matches);
    setMatches(list.matches);
    list.addEventListener('change', onChange);
    return () => list.removeEventListener('change', onChange);
  }, [query]);

  return matches;
}

export const useIsPhone = (): boolean => useMediaQuery(MEDIA.phone);
export const useIsDesktop = (): boolean => useMediaQuery(MEDIA.desktopUp);
export const useIsWide = (): boolean => useMediaQuery(MEDIA.wideUp);
export const usePrefersReducedMotion = (): boolean => useMediaQuery(MEDIA.reducedMotion);

/** Persisted UI preference, for example the collapsed sidebar. Never used for data. */
export function useLocalPreference<T>(key: string, initial: T): [T, (next: T) => void] {
  const [value, setValue] = useState<T>(() => {
    try {
      const raw = window.localStorage.getItem(key);
      return raw === null ? initial : (JSON.parse(raw) as T);
    } catch {
      return initial;
    }
  });

  const set = useCallback(
    (next: T) => {
      setValue(next);
      try {
        window.localStorage.setItem(key, JSON.stringify(next));
      } catch {
        // A private window with storage blocked is not a reason to break the screen.
      }
    },
    [key],
  );

  return [value, set];
}

/** True while the tab is visible. The /changes loop pauses when it is not. */
export function useDocumentVisible(): boolean {
  const [visible, setVisible] = useState(
    () => typeof document === 'undefined' || document.visibilityState === 'visible',
  );
  useEffect(() => {
    const onChange = () => setVisible(document.visibilityState === 'visible');
    document.addEventListener('visibilitychange', onChange);
    return () => document.removeEventListener('visibilitychange', onChange);
  }, []);
  return visible;
}

function isTypingTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  const tag = target.tagName.toLowerCase();
  return (
    tag === 'input' ||
    tag === 'textarea' ||
    tag === 'select' ||
    target.isContentEditable ||
    target.getAttribute('role') === 'combobox'
  );
}

export { isTypingTarget };

export interface ListNavigationOptions {
  count: number;
  onOpen?: (index: number) => void;
  onToggleSelect?: (index: number) => void;
  enabled?: boolean;
}

/**
 * j and k move, Enter opens, x toggles selection. Bound to the container, so the keys only
 * fire when focus is inside the list and never while the operator is typing.
 */
export function useListNavigation({
  count,
  onOpen,
  onToggleSelect,
  enabled = true,
}: ListNavigationOptions) {
  const [index, setIndex] = useState(0);
  const containerRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (index > count - 1) setIndex(Math.max(0, count - 1));
  }, [count, index]);

  const onKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if (!enabled || count === 0) return;
      if (isTypingTarget(event.target)) return;
      if (event.metaKey || event.ctrlKey || event.altKey) return;

      if (event.key === 'j' || event.key === 'ArrowDown') {
        event.preventDefault();
        setIndex((current) => Math.min(count - 1, current + 1));
      } else if (event.key === 'k' || event.key === 'ArrowUp') {
        event.preventDefault();
        setIndex((current) => Math.max(0, current - 1));
      } else if (event.key === 'Enter') {
        event.preventDefault();
        onOpen?.(index);
      } else if (event.key === 'x') {
        event.preventDefault();
        onToggleSelect?.(index);
      }
    },
    [count, enabled, index, onOpen, onToggleSelect],
  );

  return { index, setIndex, onKeyDown, containerRef };
}

/**
 * Focus trap for drawers and dialogs. Traps Tab, restores focus on unmount, and leaves Esc
 * to the caller so a dirty form can ask before closing.
 */
export function useFocusTrap(active: boolean) {
  const ref = useRef<HTMLElement | null>(null);
  const previous = useRef<HTMLElement | null>(null);

  useLayoutEffect(() => {
    if (!active) return;
    previous.current = document.activeElement as HTMLElement | null;
    const node = ref.current;
    if (!node) return;

    const focusables = () =>
      Array.from(
        node.querySelectorAll<HTMLElement>(
          'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])',
        ),
      ).filter((el) => el.offsetParent !== null || el === document.activeElement);

    const first = focusables()[0] ?? node;
    first.focus({ preventScroll: true });

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Tab') return;
      const items = focusables();
      if (items.length === 0) return;
      const firstItem = items[0];
      const lastItem = items[items.length - 1];
      if (!firstItem || !lastItem) return;
      if (event.shiftKey && document.activeElement === firstItem) {
        event.preventDefault();
        lastItem.focus();
      } else if (!event.shiftKey && document.activeElement === lastItem) {
        event.preventDefault();
        firstItem.focus();
      }
    };

    node.addEventListener('keydown', onKeyDown);
    return () => {
      node.removeEventListener('keydown', onKeyDown);
      previous.current?.focus({ preventScroll: true });
    };
  }, [active]);

  return ref;
}

/** A ticking clock so relative ages advance without every card polling. */
export function useNow(intervalMs = 15_000): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), intervalMs);
    return () => window.clearInterval(id);
  }, [intervalMs]);
  return now;
}
